/**
 * "Find a resident": the resident directory, as the server answers it
 * (docs/design/PATIENT_RECORD_SEARCH.md sections 2c and 3).
 *
 * Three calls, all under /resident-directory:
 *   GET  /access               whether the entry is offered at all
 *   POST /search               the term travels in the BODY, never in a URL (it is a resident's name)
 *   GET  /residents/:patientId the read-only record
 *
 * Nothing here writes. A refusal is `{ success: false, status, code, message }`; it is turned into words
 * that say what to do next, and never into an empty result.
 */
import { apiRequest } from '../../services/api';

export type TermKind = 'NAME' | 'BUILDING' | 'COUNSELEAR_ID' | 'CASE_REF';
export type MatchedOn = 'NAME' | 'BUILDING' | 'COUNSELEAR_ID' | 'CASE_REF';

export type PaOwnerState = 'HELD' | 'UNASSIGNED' | 'CLOSED' | 'NONE';
// RECEIVED: Finance holds the handoff record and nobody has accepted it yet.
export type FinanceOwnerState = 'ACCEPTED' | 'RECEIVED' | 'HANDED_NOT_RECEIVED' | 'NONE';

export interface DirectoryOwners {
  pa: { name: string | null; state: PaOwnerState };
  pa_facility: string | null;
  umed: string | null;
  finance: { name: string | null; state: FinanceOwnerState };
  providers: string[];
}

export interface DirectoryStages { pa: string | null; umed: string | null; finance: string | null }

export interface DirectoryRow {
  patient_id: string;
  name: string | null;
  name_source: 'RESIDENT' | 'NONE';
  birth_year: number | null;
  dob_match?: boolean;
  counselear_id: string | null;
  building: { id: string; name: string } | null;
  clinic: { id: string; name: string | null } | null;
  resident_status: string | null;
  matched_on: MatchedOn;
  owners: DirectoryOwners;
  stages: DirectoryStages;
}

export interface SearchPage { items: DirectoryRow[]; total: number; page: number; limit: number; term_kind: TermKind }

export interface DirectoryAccess { enabled: boolean; can_search: boolean }

export interface PaSection {
  case_id: string; reference: string | null; open: boolean; stage: string | null; status_word: string | null;
  disposition: string | null; opened_on: string | null; closed_on: string | null; closed_reason: string | null;
  holder: string | null; last_contact_on: string | null;
}

export interface UmedSection {
  case_id: string; reference: string | null; stage: string | null; status_word: string | null;
  exception: string | null; exception_state: string | null; operator: string | null;
  approval: { noca: string | null; flmmis: string | null; proven: boolean };
  correction: { type: string | null; state: string } | null;
  reversal: { state: string } | null;
  submitted_on: string | null; approved_on: string | null; finance_state: string | null;
}

export interface FinanceSection {
  handoff_id: string | null; state: string | null; handed_not_received: boolean; accepted_by: string | null;
  accepted_at: string | null; payment_state: string | null;
  terms: { term_months: number | null; monthly_cents: number | null; total_cents: number | null } | null;
  closed_at: string | null; close_reason: string | null;
  resubmittal: { state: string; kind: string | null } | null;
}

export interface DirectoryRecord {
  patient_id: string;
  requested_id: string;
  merged_from: string | null;
  header: {
    name: string | null; name_source: 'RESIDENT' | 'NONE'; dob: string | null; birth_year: number | null;
    counselear_id: string | null; building: { id: string; name: string } | null;
    clinic: { id: string; name: string | null } | null; resident_status: string | null;
  };
  owners: DirectoryOwners;
  stages: DirectoryStages;
  sections: { pa?: PaSection; umed?: UmedSection; finance?: FinanceSection };
  sections_shown: string[];
  links: { pa?: string; umed?: string; finance?: string };
}

export interface DirectoryResult<T> { ok: boolean; data?: T; message?: string; code?: string; status?: number }

export const PAGE_SIZE = 25;
export const MIN_TERM = 3;

export const MSG_SSN = 'Search by name, CounselEAR id or case reference. SSNs are not accepted here.';
export const MSG_SHORT = 'Type at least 3 characters (3 characters minimum).';
export const MSG_FORBIDDEN = 'You do not have access to resident search.';
export const MSG_RATE = 'Too many searches. Try again in a minute.';
export const MSG_OFF = 'Resident search is not available right now.';
export const MSG_NOT_AVAILABLE = 'This resident is not available to you.';
export const MSG_GENERIC = 'Resident search could not be completed. Try again.';

// Unicode-aware patterns, built at run time: the TypeScript target (ES5) refuses a /u literal; every supported browser
// and Node have the flag.
const ND = new RegExp('\\p{Nd}', 'u');
const ND_ALL = new RegExp('\\p{Nd}', 'gu');
const NOT_LETTER_OR_DIGIT = new RegExp('[^\\p{L}\\p{N}]', 'gu');

/** The ASCII value of a decimal digit of any script: digits come in runs of ten, each run starting at zero. */
function asciiDigit(ch: string): string {
  const cp = ch.codePointAt(0) as number;
  let start = cp;
  while (start > 0 && cp - start < 100 && ND.test(String.fromCodePoint(start - 1))) start -= 1;
  return String((cp - start) % 10);
}

/** NFKC (full-width digits become ASCII), then every script's decimal digits as ASCII: the server's normalisation. */
export const normaliseDigits = (t: string): string => String(t).normalize('NFKC').replace(ND_ALL, (d) => (/[0-9]/.test(d) ? d : asciiDigit(d)));

/**
 * An SSN, a Medicaid-shaped or an account number typed into the box (the server's rule, residents/directorySearch.js):
 * nine digits joined by single spaces, dots, hyphens, slashes, underscores or pluses; any run of nine or more digits
 * once spaces and punctuation are removed ("123 - 45 - 6789", "(123)45-6789", "ssn 123456789"); or a whole term of 10
 * or 11 digits. Refused here so it is never sent; the server refuses it again.
 */
export function looksLikeSsn(raw: string): boolean {
  const s = normaliseDigits(raw).trim();
  if (/(?:^|[^\d])\d{3}[ .\-/_+]?\d{2}[ .\-/_+]?\d{4}(?![\d])/.test(s)) return true;
  if (/\d{9,}/.test(s.replace(NOT_LETTER_OR_DIGIT, ''))) return true;
  return /^\d{10,11}$/.test(s);
}

/** The checks that need no server: a refusal here sends nothing. */
export function checkTerm(raw: string): string | null {
  const q = normaliseDigits(raw).trim();
  if (looksLikeSsn(q)) return MSG_SSN;
  if (q.length < MIN_TERM) return MSG_SHORT;
  return null;
}

type Raw = { success: boolean; data?: any; message?: string; error?: string; code?: string; status?: number } | undefined;

/** The server's refusal, in the words the business uses. */
export function searchRefusal(r: { code?: string; status?: number; message?: string }): string {
  switch (r.code) {
    case 'SSN_NOT_ACCEPTED': return MSG_SSN;
    case 'TERM_TOO_SHORT': return MSG_SHORT;
    case 'WILDCARD_ONLY': return 'Add a letter or a number to search; a wildcard on its own is not accepted.';
    case 'BAD_DOB': return 'That date of birth is not valid. Use a full date, or leave it blank.';
    case 'SEARCH_RATE_LIMITED': return MSG_RATE;
    case 'RESIDENT_DIRECTORY_OFF':
    case 'RESIDENT_DIRECTORY_NOT_READY': return MSG_OFF;
    default: break;
  }
  if (r.status === 403) return MSG_FORBIDDEN;
  if (r.status === 429) return MSG_RATE;
  if (r.status === 503) return MSG_OFF;
  return r.status === undefined ? 'Could not reach eMerge. Try again.' : MSG_GENERIC;
}

async function call<T>(method: 'GET' | 'POST', url: string, body?: unknown): Promise<{ raw: Raw; threw: boolean }> {
  try {
    const raw = (await apiRequest<T>(method, url, body)) as Raw;
    return { raw, threw: false };
  } catch {
    return { raw: undefined, threw: true };
  }
}

export const residentDirectoryApi = {
  /** The entry is offered only when this says enabled and can_search; any failure means "not offered". */
  async access(): Promise<DirectoryAccess | null> {
    const { raw } = await call<DirectoryAccess>('GET', '/resident-directory/access');
    if (!raw || !raw.success || !raw.data) return null;
    return { enabled: raw.data.enabled === true, can_search: raw.data.can_search === true };
  },

  /** POST: the term and the date of birth are in the body so they never reach a URL, a log or a referrer. */
  async search(q: string, dob: string | null, page: number): Promise<DirectoryResult<SearchPage>> {
    const body: { q: string; dob?: string; page: number; limit: number } = { q: q.trim(), page, limit: PAGE_SIZE };
    if (dob) body.dob = dob;
    const { raw, threw } = await call<SearchPage>('POST', '/resident-directory/search', body);
    if (threw || !raw) return { ok: false, message: 'Could not reach eMerge. Try again.' };
    if (!raw.success || !raw.data) return { ok: false, code: raw.code, status: raw.status, message: searchRefusal(raw) };
    return { ok: true, data: raw.data as SearchPage };
  },

  async record(patientId: string): Promise<DirectoryResult<DirectoryRecord>> {
    const { raw, threw } = await call<DirectoryRecord>('GET', `/resident-directory/residents/${encodeURIComponent(patientId)}`);
    if (threw || !raw) return { ok: false, message: 'Could not reach eMerge. Try again.' };
    if (!raw.success || !raw.data) {
      if (raw.status === 404 || raw.code === 'NOT_FOUND') return { ok: false, code: raw.code, status: 404, message: MSG_NOT_AVAILABLE };
      return { ok: false, code: raw.code, status: raw.status, message: searchRefusal(raw) };
    }
    return { ok: true, data: raw.data as DirectoryRecord };
  },
};
